import { useEffect, useRef, useState } from "react";
import { bulkInviteUsers } from "../../services/organizationService";
import { downloadBulkUserTemplate } from "../../utils/csv-templates";

interface BulkUploadResult {
  total: number;
  success: number;
  failed: number;
  results: Array<{ email: string; success: boolean; message?: string }>;
}

function partialUploadResult(error: unknown): BulkUploadResult | null {
  if (!error || typeof error !== "object" || !("partialResult" in error)) {
    return null;
  }
  const result = (error as { partialResult?: BulkUploadResult }).partialResult;
  return result && result.total > 0 ? result : null;
}

export const useBulkUploadDialogHook = (
  organisationId: string | undefined,
  onSuccess: (
    showSuccessNotification?: boolean,
  ) => boolean | void | Promise<boolean | void>,
  onClose: () => void,
) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false);
  const uploadGenerationRef = useRef(0);
  const successCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearSuccessCloseTimer = () => {
    if (successCloseTimerRef.current) {
      clearTimeout(successCloseTimerRef.current);
      successCloseTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      uploadGenerationRef.current += 1;
      if (successCloseTimerRef.current) {
        clearTimeout(successCloseTimerRef.current);
      }
    },
    [],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith(".csv")) {
        setError("Please select a CSV file");
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith(".csv")) {
        setError("Please select a CSV file");
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError(null);
      setUploadResult(null);
    }
  };

  const handleDownloadTemplate = () => {
    downloadBulkUserTemplate();
  };

  const handleUpload = async () => {
    if (!selectedFile || !organisationId) return;

    clearSuccessCloseTimer();
    const uploadGeneration = ++uploadGenerationRef.current;
    uploadingRef.current = true;
    setUploading(true);
    setError(null);

    try {
      const result = await bulkInviteUsers(organisationId, selectedFile);
      setUploadResult(result);

      // A partial upload has already durably staged some staff. Keep its
      // failed-row report open, but refresh the parent table and licence
      // summary immediately so closing this dialog cannot leave them stale.
      if (result.success > 0 && result.failed > 0) {
        void Promise.resolve(onSuccess()).then((refreshed) => {
          if (uploadGenerationRef.current !== uploadGeneration) return;
          if (refreshed === false) {
            setError(
              "Staff were uploaded, but the staff list or licence summary could not be refreshed. Keep this result open and refresh before continuing.",
            );
          }
        });
      }

      // If all succeeded, close after a short delay
      if (result.failed === 0 && result.success > 0) {
        successCloseTimerRef.current = setTimeout(() => {
          successCloseTimerRef.current = null;
          void Promise.resolve(onSuccess()).then((refreshed) => {
            if (uploadGenerationRef.current !== uploadGeneration) return;
            if (refreshed === false) {
              setError(
                "Staff were uploaded, but the staff list or licence summary could not be refreshed. Keep this result open and refresh before continuing.",
              );
              return;
            }
            if (
              uploadGenerationRef.current === uploadGeneration &&
              !uploadingRef.current
            ) {
              handleClose();
            }
          });
        }, 2000);
      }
    } catch (err: any) {
      // A later client-side batch can fail after earlier batches have already
      // committed. Refresh the durable organisation state before showing the
      // retry path so the user does not act on a stale seat/user count. A
      // first-batch failure has no confirmed write, so refresh without the
      // success notification that accompanies confirmed uploads.
      const partialResult = partialUploadResult(err);
      void Promise.resolve(onSuccess(Boolean(partialResult?.success))).then(
        (refreshed) => {
          if (uploadGenerationRef.current !== uploadGeneration) return;
          if (refreshed === false) {
            setError(
              "The upload result is uncertain and the staff list or licence summary could not be refreshed. Keep this result open and refresh before retrying.",
            );
          }
        },
      );
      if (partialResult) setUploadResult(partialResult);
      setError(err?.message || "Failed to upload users. Please try again.");
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploadingRef.current) return;
    uploadGenerationRef.current += 1;
    clearSuccessCloseTimer();
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleTryAgain = () => {
    uploadGenerationRef.current += 1;
    clearSuccessCloseTimer();
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const canUpload = selectedFile && !uploading && !uploadResult;

  return {
    // State
    selectedFile,
    uploading,
    uploadResult,
    error,
    isDragging,
    fileInputRef,

    // Actions
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDownloadTemplate,
    handleUpload,
    handleClose,
    handleTryAgain,

    // Computed
    canUpload,
  };
};
